import { motion } from "framer-motion";
import {
  Code2,
  Search,
  Palette,
  Megaphone,
  ShieldCheck,
  PenTool,
  ArrowRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: Code2,
    title: "Website Development",
    desc: "Custom-coded, conversion-engineered websites built for speed, scale and search.",
    benefits: ["Sub-1s load times", "Pixel-perfect UI", "CMS or static"],
  },
  {
    icon: Search,
    title: "SEO & GEO",
    desc: "Rank in Google AND get cited by ChatGPT, Gemini, Perplexity & Copilot.",
    benefits: ["Technical SEO", "Generative Engine Opt.", "Topical authority"],
  },
  {
    icon: Palette,
    title: "Branding",
    desc: "Identity systems that look like they belong in the top 1% of your industry.",
    benefits: ["Logo & marks", "Brand guidelines", "Visual systems"],
  },
  {
    icon: Megaphone,
    title: "Marketing Strategy",
    desc: "Channel plans built around your unit economics — not generic playbooks.",
    benefits: ["Funnel design", "Paid + organic", "Lifecycle"],
  },
  {
    icon: ShieldCheck,
    title: "Maintenance",
    desc: "Hands-off uptime, security patches, backups and continuous optimization.",
    benefits: ["24/7 monitoring", "Weekly backups", "Speed audits"],
  },
  {
    icon: PenTool,
    title: "Content Writing",
    desc: "AI-augmented, human-edited content engineered to rank and convert.",
    benefits: ["SEO articles", "Landing copy", "Email & ads"],
  },
];

export default function CoreServices() {
  return (
    <section
      id="services"
      data-testid="core-services"
      className="section relative"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            Core Services
          </div>
          <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl mx-auto">
            A complete growth stack, under one roof.
          </h2>
          <p className="mt-5 text-white/55 max-w-2xl mx-auto">
            Six interlocking services. One unified system engineered to make
            your business the obvious choice in your market.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`service-card-${i}`}
              className="group relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-8 hover:border-cyan-500/40 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center mb-6 shadow-[0_10px_30px_rgba(0,229,255,0.25)]">
                <s.icon className="w-6 h-6 text-black" />
              </div>
              <h3 className="font-display text-2xl font-semibold">{s.title}</h3>
              <p className="mt-3 text-white/60 leading-relaxed">{s.desc}</p>
              <ul className="mt-5 space-y-2">
                {s.benefits.map((b) => (
                  <li
                    key={b}
                    className="flex items-center gap-2 text-sm text-white/70"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`service-learn-more-${i}`}
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Learn More
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>

              <div className="absolute -inset-px rounded-3xl bg-gradient-to-r from-blue-600/0 via-cyan-400/20 to-blue-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
